import * as React from 'react';
import {
  CustomComponentManager,
  EditorActionsManager,
  RemoveVirtualChildAction,
  SelectManager,
  union,
  UpdateDataAction,
  VirtualChildComponent,
  VirtualChildData,
  VirtualRootComponent,
} from '@easytwin/runtime';
import { DeleteOutlined, LocateOutlined, MorePointOutlined } from '@easyv/react-icons';
import { Dropdown, Menu, message, Modal, Tooltip } from '@easyv/react-components';
import classNames from 'classnames';
import { nanoid } from 'nanoid/non-secure';
import { CollapseBox } from '@/UIGenerator';
import { copyToClipBoard, deepClone, getClipBoard, isNil } from '@/utils';
import { useEnvCtxStore } from '@/store/EnvCtxStore';
import { DigitCopyData, useDigitCopyStore } from '@/store';
import CustomAttributeKeyForm from './CustomAttributeKeyForm';
import { ComponentBasePanel } from './ComponentBasePanel';
import CustomAttributeValueForm from './CustomAttributeValueForm';
import { Gizmo } from './Gizmo';
import { ProjectionForm } from './ProjectionForm';

interface VirtualChildDataPointProps {
  comp: VirtualChildComponent;
  compJson: VirtualChildData<any>;
  mode?: 'lonLat' | 'xyz';
}

interface VirtualRootDataPanelProps {
  comp: VirtualRootComponent;

  /**
   *  VirtualChildData类型 { id: string, name: string, customAttribute: Record<string,any>, config: { points: IVector3[] } } 请使用 VirtualChildDataPoints
   * VirtualChildData类型  { id: string, name: string, customAttribute: Record<string,any>, config: { position: IVector3 } } 请使用 VirtualChildDataPosition
   */
  ChildrenDataPanel: React.FC<VirtualChildDataPointProps>;
}

export const VirtualRootDataPanel: React.FC<VirtualRootDataPanelProps> = (props) => {
  const { comp, ChildrenDataPanel: ChildrenNode } = props;

  const enableGisMode = comp.scene.geoTransform.gisMode !== 'none';

  const { setEnvCtx, exitEnvCtx, getEnvCtx } = useEnvCtxStore();
  const { copiedData, setCopiedData } = useDigitCopyStore();

  let allAttributes: string[] = [];
  if (comp.virtualChildren.length > 0)
    allAttributes = Object.keys(comp.virtualChildren[0].customAttribute);

  const compData = comp.serialize().data;

  const childData = comp.virtualChildren.map((a) => ({ comp: a, json: a.serialize() }));

  const droplistItems = [
    {
      id: '1',
      name: '复制点位数据',
      onClick: async () => {
        const childrenData = childData.map((c) => {
          let data: DigitCopyData = {
            config: c.json.config,
          };

          console.log('copy source', data);
          return data;
        });
        await copyToClipBoard(JSON.stringify(childrenData));
        setCopiedData(deepClone(childrenData));
        message.success('已复制');
        console.log('copy ', childrenData);
      },
    },
    {
      id: '2',
      name: '复制全量数据',
      onClick: async () => {
        const childrenData = childData.map((c) => {
          return c.json;
        });

        await copyToClipBoard(JSON.stringify(childrenData));
        setCopiedData(deepClone(childrenData));
        message.success('已复制');
        console.log('复制全量数据: ', childrenData);
      },
    },
    {
      id: '3',
      name: '粘贴数据',
      onClick: async () => {
        try {
          const text = await getClipBoard(false);
          let pastedObjs: DigitCopyData[];
          // 回滚到store方案
          if (isNil(text)) {
            if (isNil(copiedData)) {
              message.warning('无复制数据');
              return;
            }
            pastedObjs = copiedData;
          } else pastedObjs = JSON.parse(text) as DigitCopyData[];
          console.log('paste data', pastedObjs);
          if (pastedObjs.length === 0) return;
          if (childData.length === 0) {
            message.error('粘贴失败: 目标需要至少一个子要素');
          }
          // 所有的子要素都有position，但是只有部分有points
          const template = deepClone(childData[0].json);

          const templateConfig = template.config;
          const srcConfig = pastedObjs[0].config;
          if (
            'points' in srcConfig === 'points' in templateConfig &&
            'position' in srcConfig &&
            'position' in templateConfig
          ) {
            const newData = deepClone(childData.map((a) => a.json));

            const digitName =
              comp.engine.getManager(CustomComponentManager).get(comp.type, comp.version)?.Settings
                .name || '未命名';

            const newObjs = pastedObjs.map((pastedObj) => {
              return Object.assign({}, template, {
                ...pastedObj,
                id: nanoid(),
                name: digitName,
              });
            });

            const keys = union(
              Object.keys(template.customAttribute),
              Object.keys(pastedObjs[0].customAttribute || []),
            );
            newData.push(...newObjs);

            for (const child of newData) {
              const temp: Record<string, unknown> = {};
              for (const key of keys) {
                key in child.customAttribute
                  ? (temp[key] = child.customAttribute[key])
                  : (temp[key] = '');
              }
              child.customAttribute = temp;
            }

            const data = deepClone(comp._data);
            data.children = newData;
            const editorActionMgr = comp.engine.getManager(EditorActionsManager);
            editorActionMgr.execute(new UpdateDataAction(comp, data));
          } else {
            message.warning('数据格式不一致，无法粘贴');
          }
        } catch (error) {
          console.error(error);
          message.error('数据格式有误');
        }
      },
    },
  ];

  const digitBaseDroplist = (
    <Menu>
      {droplistItems.map((item) => {
        return (
          <Menu.Item key={item.id} onClick={item.onClick}>
            <div className='w-full'>{item.name}</div>
          </Menu.Item>
        );
      })}
    </Menu>
  );

  return (
    <ComponentBasePanel comp={comp}>
      <CustomAttributeKeyForm allAttributes={allAttributes} comp={comp} />
      <CollapseBox
        noStyle
        disable
        showDivider
        type='area'
        title={
          <div className='flex items-center gap-x-1'>
            <span>数据</span>
            <Dropdown
              droplist={digitBaseDroplist}
              position='bl'
              trigger={'click'}
              triggerProps={{ popupAlign: { bottom: [-10, 4] } }}
            >
              <MorePointOutlined className='text-content3 hover:text-white cursor-pointer w-3 h-3' />
            </Dropdown>
          </div>
        }
      >
        <ProjectionForm
          geographicProjectionEnabled={enableGisMode}
          value={compData.positionMode}
          onChange={(value) => {
            compData.positionMode = value;
            const editorActionMgr = comp.engine.getManager(EditorActionsManager);
            editorActionMgr.execute(new UpdateDataAction(comp, compData));
          }}
        />

        {childData.map((child) => {
          const childJson = child.json;
          const childComp = child.comp;

          return (
            <CollapseBox
              type='default'
              key={childComp.id}
              title={
                <div
                  className={classNames('cursor-pointer text-white')}
                  onClick={() => {
                    comp.scene.getManager(SelectManager)?.select([comp], false);
                  }}
                >
                  {childComp.name}
                </div>
              }
              showConnectionLine
              titleExtra={
                <div className='flex gap-x-1'>
                  <Tooltip content='定位'>
                    <div>
                      <Gizmo
                        wrapperClassType='hoverIcon'
                        icon={<LocateOutlined size={14} />}
                        onClick={() => {
                          const smg = comp.scene.getManager(SelectManager);
                          if (smg) {
                            smg.select([childComp], false);
                            comp.scene.focus([childComp]);
                          }
                        }}
                      />
                    </div>
                  </Tooltip>

                  <Gizmo
                    wrapperClassType='hoverIcon'
                    icon={<DeleteOutlined size={14} />}
                    onClick={() => {
                      setEnvCtx('digitDelete');
                      Modal.confirm({
                        title: `删除 ${childComp?.name}`,
                        content: '是否删除该子要素',
                        okButtonProps: { size: 'small', status: 'danger' },
                        cancelButtonProps: { size: 'small' },
                        alignCenter: true,
                        async onOk() {
                          exitEnvCtx('digitDelete');
                          const editorActionManager = comp.engine.getManager(EditorActionsManager);
                          editorActionManager.execute(
                            new RemoveVirtualChildAction(comp, childComp.id),
                          );
                        },
                        onCancel() {
                          exitEnvCtx('digitDelete');
                        },
                      });
                    }}
                  />
                </div>
              }
            >
              <ChildrenNode comp={childComp} compJson={childJson} mode={compData.positionMode} />
              <CustomAttributeValueForm comp={childComp} attribute={childJson.customAttribute} />
            </CollapseBox>
          );
        })}
      </CollapseBox>
    </ComponentBasePanel>
  );
};
